import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { SingleCard } from "../../layouts";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";

const FORM_ID = "cancellation-reason-dialog-form";
const NAME_MAX_LENGTH = 64;

const STATUS_OPTIONS = [
  { id: true, name: "Active" },
  { id: false, name: "Inactive" },
];

/**
 * Create / edit a cancellation reason. Dialog mode when `onRequestClose` is a
 * function (used by manage-cancellation-reasons.js); standalone page otherwise.
 */
export default function CancellationReasonForm(props = {}) {
  const { reasonId: reasonIdProp, onSaved, onRequestClose } = props;
  const { search } = useLocation();

  const queryParams = new URLSearchParams(search);
  const reasonIdFromQuery = parseInt(queryParams.get("reasonId"), 10);

  const effectiveReasonId = useMemo(() => {
    if (reasonIdProp != null && reasonIdProp !== "") {
      return Number(reasonIdProp);
    }
    return Number.isFinite(reasonIdFromQuery) ? reasonIdFromQuery : null;
  }, [reasonIdProp, reasonIdFromQuery]);

  const isDialogMode = typeof onRequestClose === "function";

  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!effectiveReasonId && isDialogMode);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [systemKey, setSystemKey] = useState(null);
  const [nameError, setNameError] = useState("");

  const roles = user?.userRoles ?? [];

  useEffect(() => {
    if (!effectiveReasonId) return;

    const load = async () => {
      if (isDialogMode) setFetching(true);
      else setLoading(true);
      try {
        const res = await baseAxios.get(
          `/cancellation-reasons/${effectiveReasonId}`
        );
        const reason = res.data.reason;
        setName(reason.name ?? "");
        setIsActive(reason.is_active ?? true);
        setSystemKey(reason.system_key ?? null);
      } catch {
        toast.error("Failed to load cancellation reason", { duration: 3000 });
      } finally {
        if (isDialogMode) setFetching(false);
        else setLoading(false);
      }
    };

    load();
  }, [effectiveReasonId, isDialogMode]);

  const handleSuccess = useCallback(() => {
    if (typeof onSaved === "function") {
      onSaved();
      return;
    }
    setTimeout(() => navigate(-1), 1000);
  }, [navigate, onSaved]);

  const validateForm = useCallback(() => {
    const trimmed = String(name).trim();
    if (!trimmed) {
      setNameError("Please enter a reason name");
      return false;
    }
    if (trimmed.length > NAME_MAX_LENGTH) {
      setNameError(`Keep it to ${NAME_MAX_LENGTH} characters or fewer`);
      return false;
    }
    setNameError("");
    return true;
  }, [name]);

  const submitPayload = useCallback(async () => {
    if (effectiveReasonId) {
      const res = await baseAxios.put(
        `/cancellation-reasons/${effectiveReasonId}`,
        { name: String(name).trim(), is_active: isActive }
      );
      if (res.status === 200) {
        toast.success("Cancellation reason updated", { duration: 3000 });
        handleSuccess();
      } else {
        toast.error("Update failed, please try again", { duration: 5000 });
      }
      return;
    }

    const res = await baseAxios.post("/cancellation-reasons", {
      name: String(name).trim(),
    });
    if (res.status === 200 || res.status === 201) {
      toast.success("Cancellation reason created", { duration: 3000 });
      handleSuccess();
    } else {
      toast.error("Creation failed, please try again", { duration: 5000 });
    }
  }, [effectiveReasonId, handleSuccess, isActive, name]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!roles.includes(6) && !roles.includes(3) && !roles.includes(2)) {
        toast.error("User not authorized, please check your role", {
          duration: 5000,
        });
        return;
      }

      if (!validateForm()) return;

      setLoading(true);
      try {
        await submitPayload();
      } catch (error) {
        if (error.status === 400) {
          toast.error(error.data?.message || "Bad request", { duration: 5000 });
        } else {
          toast.error("Operation failed: " + error.message, { duration: 5000 });
        }
      } finally {
        setLoading(false);
      }
    },
    [roles, submitPayload, validateForm]
  );

  const busy = loading || fetching;

  const formFields = (
    <Stack spacing={2}>
      <TextField
        label="Reason name"
        value={name}
        onChange={(event) => {
          setName(event.target.value);
          if (nameError) setNameError("");
        }}
        required
        fullWidth
        size="small"
        disabled={busy}
        error={Boolean(nameError)}
        helperText={
          nameError ||
          (systemKey
            ? "Default reason — you can rename or deactivate it, but it stays available to the system."
            : " ")
        }
        inputProps={{ maxLength: NAME_MAX_LENGTH }}
        color="primary"
      />

      {effectiveReasonId ? (
        <FormControl fullWidth size="small" color="primary" disabled={busy}>
          <InputLabel id="cancellation-reason-status-label">Status</InputLabel>
          <Select
            labelId="cancellation-reason-status-label"
            label="Status"
            value={isActive}
            onChange={(event) => setIsActive(event.target.value)}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={String(option.id)} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
    </Stack>
  );

  if (isDialogMode) {
    return (
      <>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "secondary.main",
            color: "common.white",
            borderBottom: "1px solid",
            borderColor: "primary.main",
            px: 3,
            py: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="inherit">
              {effectiveReasonId ? "Update reason" : "Add new reason"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
            >
              {effectiveReasonId
                ? "Rename the reason or set it inactive to remove it from the cancel dialog."
                : "Create a new reason for cancelling appointments."}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onRequestClose}
            disabled={busy}
            size="small"
            sx={{
              color: "primary.main",
              mt: 0.25,
              "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {fetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} color="primary" />
            </Box>
          ) : (
            <Box component="form" id={FORM_ID} onSubmit={onSubmit} sx={{ p: 3 }}>
              {formFields}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            gap: 1,
          }}
        >
          <Button
            onClick={onRequestClose}
            disabled={busy}
            variant="outlined"
            color="secondary"
            sx={{ textTransform: "none", minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="contained"
            color="primary"
            disabled={busy || fetching}
            sx={{
              minWidth: 140,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : effectiveReasonId ? (
              "Save reason"
            ) : (
              "Create reason"
            )}
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <SingleCard
      title={
        effectiveReasonId ? "Update Cancellation Reason" : "Add Cancellation Reason"
      }
    >
      <Box component="form" onSubmit={onSubmit} sx={{ p: 1 }}>
        {formFields}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={busy}
          sx={{
            mt: 2.5,
            minHeight: 44,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : effectiveReasonId ? (
            "Save Reason"
          ) : (
            "Create Reason"
          )}
        </Button>
      </Box>
    </SingleCard>
  );
}
